// src/pages/NavigationPage.js
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Spinner, Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";

const STATUS_META = {
  recommended: { label: "Recommended", bg: "primary" },
  approved:    { label: "Approved",    bg: "success" },
  active:      { label: "Active",      bg: "info"    },
  denied:      { label: "Denied",      bg: "danger"  },
};

function effectiveStatus(c) {
  if (c.nav_status) return c.nav_status;
  return c.nav_program_recommended ? "recommended" : null;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, bg: "secondary" };
  return <Badge bg={meta.bg}>{meta.label}</Badge>;
}

export default function NavigationPage() {
  const { user } = useContext(AuthContext);
  const canAccess =
    user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR") || user?.roles?.includes("NAVIGATOR");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [counts, setCounts] = useState({ recommended: 0, approved: 0, active: 0, denied: 0 });
  const [clients, setClients] = useState([]);
  const [navigators, setNavigators] = useState([]);
  const [filter, setFilter] = useState("recommended");
  const [saving, setSaving] = useState(null); // client id currently saving
  const [denyModal, setDenyModal] = useState(null); // { client, note }

  const load = useCallback(async () => {
    setError("");
    try {
      const [navRes, usersRes] = await Promise.all([
        api.get("/navigation"),
        api.get("/navigation/users"),
      ]);
      setCounts(navRes.data.counts);
      setClients(navRes.data.clients);
      setNavigators(usersRes.data.items);
    } catch {
      setError("Failed to load navigation data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayed = useMemo(() => {
    if (filter === "all") return clients.filter((c) => effectiveStatus(c) !== "denied");
    return clients.filter((c) => effectiveStatus(c) === filter);
  }, [clients, filter]);

  async function updateClient(clientId, payload) {
    setSaving(clientId);
    setActionError("");
    try {
      await api.put(`/clients/${clientId}`, payload);
      await load();
    } catch {
      setActionError("Action failed. Please try again.");
    } finally {
      setSaving(null);
    }
  }

  async function handleApprove(c) {
    await updateClient(c.id, { nav_status: "approved" });
  }

  async function handleSetActive(c) {
    await updateClient(c.id, { nav_status: "active" });
  }

  async function handleDenyConfirm() {
    const { client, note } = denyModal;
    const existing = client.nav_program_notes || "";
    const dateStr = new Date().toLocaleDateString("en-US");
    const stamp = note.trim()
      ? `[Denied ${dateStr}]: ${note.trim()}`
      : `[Denied ${dateStr}]`;
    const newNotes = existing ? `${existing}\n${stamp}` : stamp;
    setDenyModal(null);
    await updateClient(client.id, {
      nav_status: "denied",
      nav_program_recommended: false,
      nav_program_notes: newNotes,
    });
  }

  async function handleNavigatorChange(c, value) {
    const navigatorId = value ? parseInt(value, 10) : null;
    await updateClient(c.id, { nav_navigator_id: navigatorId });
  }

  if (!canAccess) {
    return <Alert variant="danger" className="m-3">You do not have permission to view this page.</Alert>;
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <h3 className="mb-4">Navigation Program</h3>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="warning" dismissible onClose={() => setActionError("")}>{actionError}</Alert>}

      {/* Count cards */}
      <Row className="g-3 mb-4">
        {[
          { key: "recommended", label: "Recommended", variant: "primary" },
          { key: "approved",    label: "Approved",    variant: "success" },
          { key: "active",      label: "Active",      variant: "info"    },
          { key: "denied",      label: "Denied",      variant: "danger"  },
        ].map(({ key, label, variant }) => (
          <Col key={key} xs={6} md={3}>
            <Card
              className={`text-center h-100 ${filter === key ? `border-${variant} border-2` : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setFilter(key)}
            >
              <Card.Body>
                <div className="text-muted small mb-1">{label}</div>
                <div className={`fs-2 fw-bold text-${variant}`}>{counts[key]}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter tabs */}
      <Nav variant="tabs" className="mb-3">
        {[
          { key: "all",         label: "All Active" },
          { key: "recommended", label: "Recommended" },
          { key: "approved",    label: "Approved" },
          { key: "active",      label: "Active" },
          { key: "denied",      label: "Denied" },
        ].map(({ key, label }) => (
          <Nav.Item key={key}>
            <Nav.Link
              active={filter === key}
              onClick={() => setFilter(key)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {displayed.length === 0 ? (
        <p className="text-muted">No clients in this category.</p>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Client</th>
              <th>Status</th>
              <th>Navigator</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((c) => {
              const status = effectiveStatus(c);
              const isBusy = saving === c.id;
              const showNavigator = status === "approved" || status === "active";

              return (
                <tr key={c.id}>
                  <td>
                    <Link to={`/clients/${c.id}/view`} className="fw-semibold">
                      {c.first_name} {c.last_name}
                    </Link>
                    {c.intake_date && (
                      <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                        Intake: {c.intake_date}
                      </div>
                    )}
                  </td>

                  <td className="align-middle">
                    <StatusBadge status={status} />
                  </td>

                  <td className="align-middle">
                    {showNavigator ? (
                      <Form.Select
                        size="sm"
                        style={{ maxWidth: 200 }}
                        value={c.nav_navigator_id || ""}
                        disabled={isBusy}
                        onChange={(e) => handleNavigatorChange(c, e.target.value)}
                      >
                        <option value="">— Unassigned —</option>
                        {navigators.map((n) => (
                          <option key={n.id} value={n.id}>{n.full_name}</option>
                        ))}
                      </Form.Select>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  <td className="align-middle">
                    <div className="d-flex gap-2 flex-wrap">
                      {status === "recommended" && (
                        <Button
                          size="sm" variant="success"
                          disabled={isBusy}
                          onClick={() => handleApprove(c)}
                        >
                          {isBusy ? <Spinner animation="border" size="sm" /> : "Approve"}
                        </Button>
                      )}
                      {status === "approved" && (
                        <Button
                          size="sm" variant="info"
                          disabled={isBusy}
                          onClick={() => handleSetActive(c)}
                        >
                          {isBusy ? <Spinner animation="border" size="sm" /> : "Set Active"}
                        </Button>
                      )}
                      {status !== "denied" && (
                        <Button
                          size="sm" variant="outline-danger"
                          disabled={isBusy}
                          onClick={() => setDenyModal({ client: c, note: "" })}
                        >
                          Deny
                        </Button>
                      )}
                      {status === "denied" && (
                        <span className="text-muted small fst-italic">Denied</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Deny confirmation modal */}
      <Modal show={!!denyModal} onHide={() => setDenyModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deny Navigation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Denying <strong>{denyModal?.client?.first_name} {denyModal?.client?.last_name}</strong> will
            set their recommendation to <strong>No</strong> and append a note to their record.
          </p>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Reason for denial…"
              value={denyModal?.note || ""}
              onChange={(e) =>
                setDenyModal((prev) => ({ ...prev, note: e.target.value }))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDenyModal(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDenyConfirm} disabled={!!saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Confirm Deny"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
